import type {FormEvent} from "react";
import {useState} from "react";
import {BaseAlert, BaseButton, BaseInput} from "@/components";
import type {LoginFormValues} from "./login.schema";
import {loginSchema} from "./login.schema";
import {useLogin} from "./useLogin";

export interface LoginPageProps {
  onSuccess?: () => void;
}

type FieldErrors = Partial<Record<keyof LoginFormValues, string>>;

const LoginPage = ({onSuccess}: LoginPageProps) => {
  const {login, isPending, isError, error} = useLogin();
  const [values, setValues] = useState<LoginFormValues>({username: "", password: ""});
  const [errors, setErrors] = useState<FieldErrors>({});

  const updateField = <K extends keyof LoginFormValues>(field: K, value: LoginFormValues[K]) => {
    setValues(prev => ({...prev, [field]: value}));
    if (errors[field]) setErrors(prev => ({...prev, [field]: undefined}));
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = loginSchema.safeParse(values);
    if (!result.success) {
      const next: FieldErrors = {};
      for (const issue of result.error.issues) {
        const key = issue.path[0];
        if (typeof key === "string" && !next[key as keyof LoginFormValues]) {
          next[key as keyof LoginFormValues] = issue.message;
        }
      }
      setErrors(next);
      return;
    }
    login(result.data, {onSuccess: () => onSuccess?.()});
  };

  return (
    <div className="mx-auto flex w-full max-w-[420px] flex-col gap-6 rounded-[var(--radius-xl)] bg-[var(--color-surface)] p-8 shadow-[var(--shadow-lg)]">
      <header className="flex flex-col gap-2 text-center">
        <h1 className="m-0 text-[var(--font-h1)] font-bold text-[var(--color-text)]">Iniciar sesión</h1>
        <p className="m-0 text-[var(--font-body-lg)] text-[var(--color-text-secondary)]">
          Ingresa tus credenciales para continuar
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-4"
        noValidate
      >
        <BaseInput
          type="email"
          label="Correo electrónico"
          placeholder="tu@correo.com"
          autoComplete="email"
          value={values.username}
          onChange={event => updateField("username", event.target.value)}
          error={errors.username}
          fullWidth
          required
        />

        <BaseInput
          type="password"
          label="Contraseña"
          placeholder="••••••••"
          autoComplete="current-password"
          value={values.password}
          onChange={event => updateField("password", event.target.value)}
          error={errors.password}
          fullWidth
          required
        />

        {isError && <BaseAlert variant="danger">{error?.message ?? "Credenciales incorrectas"}</BaseAlert>}

        <BaseButton
          type="submit"
          variant="primary"
          size="lg"
          loading={isPending}
          fullWidth
        >
          Entrar
        </BaseButton>
      </form>
    </div>
  );
};

export default LoginPage;
